import { useEffect } from "react";
import { useRouterState } from "@tanstack/react-router";

type IdleHandle = { cancel: () => void };

/** requestIdleCallback with a setTimeout fallback for browsers that lack it (Safari). */
function whenIdle(fn: () => void, timeout = 2000): IdleHandle {
  const w = window as Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    cancelIdleCallback?: (id: number) => void;
  };
  if (w.requestIdleCallback && w.cancelIdleCallback) {
    const id = w.requestIdleCallback(fn, { timeout });
    return { cancel: () => w.cancelIdleCallback?.(id) };
  }
  const id = setTimeout(fn, 200);
  return { cancel: () => clearTimeout(id) };
}

const SLICE = 50;
const RISE_SELECTOR = ".section-num, .eyebrow, h2, p, .bhg-btn, li, figure, img, article, details, blockquote, table";

/**
 * Progressive enhancement only. Server HTML and first-paint content stay visible.
 * Starts after load + idle; decides "below the fold" exclusively through IntersectionObserver
 * (no getBoundingClientRect / getComputedStyle before the first idle callback).
 */
export function ScrollMotion() {
  const path = useRouterState({ select: (state) => state.location.pathname });
  useEffect(() => {
    // Reading pages retain their complete first-paint state without decorative hiding or count mutations.
    if (document.querySelector("main.centered-reading")) return;
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (media.matches) return;

    let stopped = false;
    const idles = new Set<IdleHandle>();
    const frames = new Set<number>();
    const timers = new Set<ReturnType<typeof setTimeout>>();
    const originals = new Map<HTMLElement, string>();
    const decorated = new Set<HTMLElement>();
    const lines = new Set<HTMLElement>();
    const counts = new Map<Element, () => void>();
    let classifier: IntersectionObserver | null = null;
    let reveal: IntersectionObserver | null = null;
    let onScroll: (() => void) | null = null;

    const idle = (fn: () => void) => {
      const handle = whenIdle(() => { idles.delete(handle); if (!stopped) fn(); });
      idles.add(handle);
    };
    const frame = (fn: FrameRequestCallback) => {
      const id = requestAnimationFrame((now) => { frames.delete(id); if (!stopped) fn(now); });
      frames.add(id);
    };
    /** Run fn over items in idle-time slices of SLICE elements. */
    const sliced = <T,>(items: T[], fn: (item: T, index: number) => void, done?: () => void) => {
      let i = 0;
      const step = () => {
        const end = Math.min(items.length, i + SLICE);
        for (; i < end; i++) fn(items[i], i);
        if (i < items.length) idle(step); else done?.();
      };
      idle(step);
    };
    const mark = (el: HTMLElement, ...classes: string[]) => { decorated.add(el); el.classList.add(...classes); };

    type Kind = { type: "rise"; group: Element } | { type: "underline" } | { type: "count" } | { type: "census" };
    const kinds = new Map<Element, Kind>();
    const groupIndex = new Map<Element, number>();

    const setupCount = (el: HTMLElement) => {
      const original = (el.textContent ?? "").trim();
      const match = /^(\$?)([\d,]+(?:\.\d+)?)(M\+|%|x|\+)?$/.exec(original);
      if (!match) return false;
      const digits = match[2] ?? "0";
      const target = Number(digits.replaceAll(",", ""));
      const decimals = digits.split(".")[1]?.length ?? 0;
      const format = (value: number) => `${match[1] ?? ""}${value.toLocaleString("en-US", { useGrouping: digits.includes(","), minimumFractionDigits: decimals, maximumFractionDigits: decimals })}${match[3] ?? ""}`;
      originals.set(el, original);
      // Keep original content until intersection, including for assistive technology.
      counts.set(el, () => {
        const start = performance.now();
        const tick = (now: number) => {
          const p = Math.min(1, (now - start) / 1400);
          el.textContent = p === 1 ? original : format(target * (1 - (1 - p) ** 3));
          if (p < 1) frame(tick);
        };
        frame(tick);
      });
      return true;
    };

    const setupCensus = (grid: HTMLElement) => {
      const mobile = window.matchMedia("(max-width: 767px)").matches;
      const cols = Number(mobile ? grid.dataset.colsMobile : grid.dataset.cols) || 27;
      const dots = Array.from(grid.querySelectorAll<HTMLElement>(".census-dot"));
      dots.forEach((dot, index) => {
        dot.style.setProperty("--dot-delay", `${(index % cols) * 12 + Math.floor(index / cols) * 30}ms`);
      });
      mark(grid, "census-animated");
    };

    const start = () => {
      const excluded = new Set<Element>();
      document.querySelectorAll('header, footer, .bhg-marquee-container, [data-motion-exclude], [class*="marquee"], .track, [class$="-track"], [class*="-track "]').forEach((el) => excluded.add(el));
      const checked = new Set<Element>();
      // Ancestor styles are checked once, only after load + idle. Horizontal tracks
      // must never be observed or hidden, including a wrapper that contains them.
      const checkTracks = (el: Element) => {
        for (let parent = el.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
          if (checked.has(parent)) break;
          checked.add(parent);
          const style = getComputedStyle(parent);
          if (style.animationName.split(",").some((name) => name.trim() !== "none")) excluded.add(parent);
          if ((style.overflowX === "auto" || style.overflowX === "scroll") && parent.scrollWidth > parent.clientWidth) excluded.add(parent);
        }
      };
      const skip = (el: Element) => {
        checkTracks(el);
        return [...excluded].some((root) => root.contains(el) || el.contains(root));
      };

      reveal = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || stopped) continue;
          const el = entry.target as HTMLElement;
          el.classList.add("motion-shown");
          counts.get(el)?.();
          counts.delete(el);
          if (el.matches(".tracking-census")) {
            const timer = setTimeout(() => { if (!stopped) el.classList.add("census-flipped"); }, 1100);
            timers.add(timer);
          }
          reveal?.unobserve(el);
        }
      }, { rootMargin: "0px 0px -12% 0px" });

      // First report per element: already on screen (or not rendered) = leave alone; otherwise decorate.
      classifier = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          const el = entry.target as HTMLElement;
          classifier?.unobserve(el);
          const kind = kinds.get(el);
          kinds.delete(el);
          if (!kind || stopped) continue;
          const rect = entry.boundingClientRect;
          const rendered = rect.width > 0 || rect.height > 0;
          // Vertical position only (horizontally scrolled items such as the marquee count as on screen).
          const onScreen = entry.isIntersecting || rect.top <= (entry.rootBounds?.bottom ?? innerHeight * .92);
          if (kind.type === "underline") {
            if (onScreen || !rendered) mark(el, "motion-underline", "motion-shown");
            else { mark(el, "motion-underline"); reveal?.observe(el); }
            continue;
          }
          if (onScreen || !rendered) continue;
          if (kind.type === "rise") {
            if (el.closest(".motion-rise")) continue;
            const index = groupIndex.get(kind.group) ?? 0;
            groupIndex.set(kind.group, index + 1);
            el.style.setProperty("--motion-delay", `${Math.min(index, 8) * 70}ms`);
            mark(el, "motion-rise");
            reveal?.observe(el);
          } else if (kind.type === "count") {
            if (setupCount(el)) reveal?.observe(el);
          } else if (kind.type === "census") {
            setupCensus(el);
            reveal?.observe(el);
          }
        }
      }, { rootMargin: "0px 0px -8% 0px" });

      const queue: Array<[HTMLElement, Kind]> = [];
      document.querySelectorAll<HTMLElement>("main section, main > .wrap, main .blog-post-wrap, main .glossary-reading").forEach((group) => {
        group.querySelectorAll<HTMLElement>(RISE_SELECTOR).forEach((el) => {
          if (el.closest("[hidden]") || skip(el)) return;
          queue.push([el, { type: "rise", group }]);
        });
      });
      document.querySelectorAll<HTMLElement>("main h2 .display-italic").forEach((el) => { if (!skip(el)) queue.push([el, { type: "underline" }]); });
      document.querySelectorAll<HTMLElement>("main [data-countup]").forEach((el) => { if (!skip(el)) queue.push([el, { type: "count" }]); });
      document.querySelectorAll<HTMLElement>("main .tracking-census").forEach((el) => { if (!skip(el)) queue.push([el, { type: "census" }]); });
      const seen = new Set<HTMLElement>();
      sliced(queue, ([el, kind]) => {
        if (seen.has(el) || skip(el)) return;
        seen.add(el);
        kinds.set(el, kind);
        classifier?.observe(el);
      });

      const steps = Array.from(document.querySelectorAll<HTMLElement>("main .mono"));
      sliced(steps, (el) => {
        if (/^STEP 0[1-4]$/.test((el.textContent ?? "").trim()) && el.parentElement && !skip(el)) {
          lines.add(el.parentElement); mark(el.parentElement, "motion-step");
        }
      }, () => update());

      let scrolling = false;
      const update = () => {
        if (scrolling) return;
        scrolling = true;
        frame(() => {
          scrolling = false;
          lines.forEach((el) => el.style.setProperty("--step-progress", String(Math.max(0, Math.min(1, (innerHeight * .85 - el.getBoundingClientRect().top) / (innerHeight * .35))))));
        });
      };
      onScroll = update;
      window.addEventListener("scroll", update, { passive: true });
      update();
    };

    const begin = () => idle(start);
    if (document.readyState === "complete") begin();
    else window.addEventListener("load", begin, { once: true });

    const cleanup = () => {
      stopped = true;
      window.removeEventListener("load", begin);
      idles.forEach((h) => h.cancel());
      classifier?.disconnect();
      reveal?.disconnect();
      frames.forEach(cancelAnimationFrame);
      timers.forEach(clearTimeout);
      if (onScroll) window.removeEventListener("scroll", onScroll);
      decorated.forEach((el) => { el.classList.remove("motion-rise", "motion-shown", "motion-underline", "motion-step", "census-animated", "census-flipped"); el.style.removeProperty("--motion-delay"); el.style.removeProperty("--step-progress"); });
      originals.forEach((text, el) => { el.textContent = text; });
    };
    media.addEventListener("change", cleanup, { once: true });
    return () => { cleanup(); media.removeEventListener("change", cleanup); };
  }, [path]);
  return null;
}
